/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { contextPath, uploadTimeout } from '../settings';
import { httpRequest } from '../shared/http';
import { sanitize } from '../shared/utils/objectUtils';
import { downloadFile } from '../shared/utils/attachmentUtils';

const baseUrl = '/npefiling-api/jsonschema/submissions';
const submissionsBaseUrl = '/npefiling-api/submissions';

export default {
  create: async (submission, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}`, {
      method: 'POST',
      data: sanitize(submission),
      ...options,
    })();
    return sanitize(resp);
  },

  update: async (submissionId, submission, options) => {
    const submissionToUpdate = sanitize(submission);
    submissionToUpdate.id = submissionId;
    const { data: resp } = await httpRequest(`${baseUrl}/${submissionId}`, {
      method: 'PUT',
      data: submissionToUpdate,
      ...options,
    })();
    return sanitize(resp);
  },

  clone: async (submissionId, options = {}) => {
    const { data: resp } = await httpRequest(`${baseUrl}/clone`, {
      method: 'POST',
      ...options,
      params: {
        submissionId,
        ...options.params,
      },
    })();
    return sanitize(resp);
  },

  rollbackStatus: async (submissionId, submissionStatusChange, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/status/${submissionId}/rollback`, {
      method: 'PATCH',
      data: submissionStatusChange,
      ...options,
    })();
    return sanitize(resp);
  },

  getById: async (draftId, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/${draftId}`, {
      method: 'GET',
      ...options,
    })();
    return resp;
  },

  validateBusinessRules: async (submission, params, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/validate`, {
      method: 'POST',
      data: sanitize(submission),
      params,
      ...options,
    })();
    return resp;
  },

  postProcessBusinessRules: async (submission, event, id, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/post-process`, {
      method: 'POST',
      data: sanitize(submission),
      params: {
        event,
        id,
      },
      ...options,
    })();
    return resp;
  },

  openSubmission: async (submissionId, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/${submissionId}/overview`, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
      },
      responseType: 'blob',
      ...options,
    })();

    try {
      const file = new Blob([resp], { type: ' application/pdf' });
      const fileURL = URL.createObjectURL(file);
      return fileURL;
    } catch (err) {
      return err;
    }
  },

  validateExistingUserReference: async (draft, options) => {
    const { userReference } = draft;
    const { id } = draft;
    const { data: resp } = await httpRequest(`${baseUrl}/reference`, {
      method: 'GET',
      params: {
        userReference: encodeURIComponent(userReference),
        id: encodeURIComponent(id),
      },
      ...options,
    })();
    return resp;
  },

  validateSubmission: async (draftId, isLocked, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/${draftId}/validate/signatures`, {
      method: 'GET',
      params: {
        fromDraft: isLocked,
      },
      ...options,
    })();
    return resp;
  },

  runCustomRule: async (submission, ruleName, params, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/custom-rule/${ruleName}`, {
      method: 'POST',
      data: sanitize(submission),
      params,
      ...options,
    })();
    return resp;
  },

  fetchSubmissionRows: async (procedure, initialSubmissionProcedure, pagingSortingAndFiltering, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/filter`, {
      method: 'POST',
      ...options,
      params: {
        page: Math.max(0, pagingSortingAndFiltering.pageNumber) || 0,
        size: pagingSortingAndFiltering.numberOfElementsOnAPage,
        sort:
          pagingSortingAndFiltering.sortColumn && pagingSortingAndFiltering.sortOrder
            ? `${pagingSortingAndFiltering.sortColumn},${pagingSortingAndFiltering.sortOrder}`
            : undefined,
        procedure,
        initialSubmissionProcedure,
      },
      data: { filter: pagingSortingAndFiltering.filter },
    })();
    return resp;
  },

  uploadAttachment: async (draftId, data, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${draftId}/attachments`, {
      method: 'POST',
      data,
      headers: {
        'Content-Type': 'multipart/mixed',
      },
      timeout: uploadTimeout,
      ...options,
    })();
    return resp;
  },
  deleteAttachment: async (draftId, attachmentId, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${draftId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      ...options,
    })();
    return resp;
  },
  deleteAttachments: async (draftId, attachmentIds, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${draftId}/attachments`, {
      method: 'DELETE',
      params: attachmentIds,
      ...options,
    })();
    return resp;
  },

  // TODO: remove draftId from this api call. it's not used for single files
  fetchAttachment: async (draftId, attachmentId, originalFileName, mimeType, options) => {
    const response = await httpRequest(`${submissionsBaseUrl}/${draftId}/attachments/${attachmentId}/content/file`, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream',
      },
      responseType: 'blob',
      ...options,
    })();
    try {
      const file = new Blob([response.data], {
        type: mimeType,
        originalFileName,
      });
      return file;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      return err;
    }
  },
  getFees: async (options) => {
    const { data: response } = await httpRequest(`${contextPath}/npefiling-api/fees`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      ...options,
    })();
    return response;
  },
  openFile: async (submissionId, fileId, originalFileName, mimeType, receiptString, options) => {
    const response = await httpRequest(`${baseUrl}/${submissionId}/files/${fileId}${receiptString || ''}`, {
      method: 'GET',
      headers: {
        Accept: mimeType || 'application/octet-stream',
      },
      responseType: 'blob',
      ...options,
    })();
    try {
      const file = new Blob([response.data], {
        type: mimeType,
        originalFileName,
      });
      const fileURL = URL.createObjectURL(file);
      return [fileURL, file];
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      return err;
    }
  },

  lockById: async (draftId, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/${draftId}/lock`, {
      method: 'PATCH',
      ...options,
    })();
    return sanitize(resp);
  },
  unlockById: async (draftId, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/${draftId}/unlock`, {
      method: 'PATCH',
      ...options,
    })();
    return resp;
  },
  sendToFiling: async (draftId, fromDraft, options) => {
    const response = await httpRequest(`${submissionsBaseUrl}/${draftId}/send-to-filing`, {
      method: 'PUT',
      ...options,
    })();
    return response;
  },
  sendToSign: async (draftId, fromDraft, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/${draftId}/signatures`, {
      method: 'PATCH',
      params: {
        fromDraft,
      },
      ...options,
    })();
    return resp;
  },
  sendToSignSubmissions: async (draftIds, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/signatures`, {
      method: 'PATCH',
      data: draftIds,
      ...options,
    })();
    return resp;
  },
  addSignature: async (submissionId, data, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${submissionId}/signing`, {
      method: 'PUT',
      data,
      ...options,
    })();
    return resp;
  },
  removeSignature: async (submissionId, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${submissionId}/signing`, {
      method: 'DELETE',
      ...options,
    })();
    return resp;
  },
  submitSignatures: async (submissionId, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${submissionId}/signing`, {
      method: 'POST',
      ...options,
    })();
    return resp;
  },
  retrySigning: async (submissionId, options) => {
    const response = await httpRequest(`${submissionsBaseUrl}/${submissionId}/signing/retry`, {
      method: 'POST',
      ...options,
    })();
    return response;
  },
  deleteSubmissions: async (submissionIds, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}`, {
      method: 'DELETE',
      data: submissionIds,
      ...options,
    })();
    return resp;
  },
  getSubmissionDashboardInfo: async (procedure, subsequentActionsLabel, statuses, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/dashboard/${procedure}`, {
      method: 'GET',
      params: {
        subsequentActionsLabel,
        status: statuses,
      },
      ...options,
    })();
    return resp;
  },
  getPackageMetadata: async (submissionId, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${submissionId}/package`, {
      method: 'GET',
      ...options,
    })();
    return resp;
  },
  downloadPackageContent: async (submissionId, filename, options) => {
    const response = await httpRequest(`${submissionsBaseUrl}/${submissionId}/package/content`, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream',
      },
      responseType: 'blob',
      ...options,
    })();
    try {
      const file = new Blob([response.data], { type: 'application/zip' });
      downloadFile(URL.createObjectURL(file), filename);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  },
  resendSigningEmails: async (submissionId, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${submissionId}/signing/resend-emails`, {
      method: 'GET',
      ...options,
    })();
    return resp;
  },
  requestReceipt: async (submissionId, email, options) => {
    const { data: resp } = await httpRequest(`${submissionsBaseUrl}/${submissionId}/request-receipt`, {
      method: 'PUT',
      data: email,
      ...options,
    })();
    return resp;
  },
  transferSubmissions: async (submissionIds, newOrganizationId, options) => {
    const { data: resp } = await httpRequest(`${baseUrl}/transfer/${newOrganizationId}`, {
      method: 'PATCH',
      data: submissionIds,
      ...options,
    })();
    return resp;
  },
};
